import { uiText, useUiLanguage } from '@/i18n/interface';
import warriorHubLogo from '@/assets/warriorhub-logo.png';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Shield, LogOut, Users, Plus, LifeBuoy } from 'lucide-react';
import Layout from '../components/Layout';
import AdminStats from '../components/admin/AdminStats';
import AdminQuickActions from '../components/admin/AdminQuickActions';
import AdminDashboardHeader from '../components/admin/AdminDashboardHeader';
import AdminNavigation from '../components/admin/AdminNavigation';
import AdminTabs from '../components/admin/AdminTabs';
import AdminDialogs from '../components/admin/AdminDialogs';
import TrialCountdownTimer from '../components/TrialCountdownTimer';
import { useSessionManagement } from '../hooks/useSessionManagement';
import { useAdminStats } from '../hooks/useAdminStats';
import { useCurrentDojo } from '../hooks/useCurrentDojo';
import { useTrialStatus } from '../hooks/useTrialStatus';
import DashboardOverview from '@/components/admin/DashboardOverview';
import { adminSections } from '@/components/admin/adminSections';

const AdminDashboard = () => {
  useUiLanguage();
  const { profile } = useAuth();
  const { dojo } = useCurrentDojo();
  const { isAccountLocked } = useTrialStatus();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { impersonation, stopImpersonation } = useAuth();
  const [newSessionDialogOpen, setNewSessionDialogOpen] = useState(false);
  const [studentsListDialogOpen, setStudentsListDialogOpen] = useState(false);
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') || 'overview';
  const activeTab = adminSections.some(s => s.value === requestedTab) ? requestedTab : 'overview';
  const setActiveTab = (tab: string) => setSearchParams(params => { params.set('tab', tab); return params; });
  const [paymentTab, setPaymentTab] = useState('categories');
  const { stats, isLoadingStats } = useAdminStats();
  const { fetchTrainingSessions } = useSessionManagement();

  // Redirect to trial expired if account is locked
  if (isAccountLocked) {
    return null; // The main App component will handle showing TrialExpiredScreen
  }

  // Dummy handler for QuickActions, replace with actual logic if needed
  const handleQuickAction = (action) => {
    console.log("Quick action:", action);
    // Example: navigate(`/admin/${action}`);
  };

  return (
    <>
      {/* Impersonation Banner */}
      {impersonation.active && (
        <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-4">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              <span className="font-semibold">
                {uiText("IMPERSONATION MODE: Viewing as admin of")}{' '}{impersonation.dojoName}
              </span>
            </div>
            <Button
              onClick={async () => {
                try {
                  stopImpersonation();
                  // Small delay to ensure state is updated
                  setTimeout(() => {
                    navigate('/super-user', { replace: true });
                  }, 100);
                } catch (error) {
                  console.error('Error exiting impersonation:', error);
                  navigate('/super-user', { replace: true });
                }
              }}
              variant="outline"
              size="sm"
              className="bg-white/20 text-white border-white/30 hover:bg-white/30"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {uiText("Exit Impersonation")}</Button>
          </div>
        </div>
      )}

      <Layout title={t('admin:studio.workspace', { defaultValue: 'Dojo workspace' })}>
        <div className="wh-workspace">
          <aside className="wh-workspace-sidebar">
            <div className="wh-sidebar-heading"><img src={warriorHubLogo} alt={uiText('WarriorHub Logo')} className="wh-sidebar-emblem" /><div><strong>{uiText("Dojo control")}</strong><span>{uiText("WarriorHub workspace")}</span></div></div>
            <p className="wh-sidebar-label">{uiText("WORKSPACE")}</p>
            <AdminNavigation activeTab={activeTab} onTabChange={setActiveTab} />
            <div className="wh-sidebar-footer"><p>{uiText("YOUR DOJO, CONNECTED")}</p><span>{uiText("People. Training. Progress.")}</span><Button variant="ghost" onClick={()=>setStudentsListDialogOpen(true)}><Users />{uiText("View all students")}</Button></div>
          </aside>
          <div className="wh-workspace-main space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3"><p className="wh-eyebrow">{t('admin:studio.workspace',{defaultValue:'Dojo workspace'})}<span className="mx-2 opacity-40">/</span><span className="text-foreground">{adminSections.find(s=>s.value===activeTab)?.fallback}</span></p><div className="wh-toolbar"><Button variant="outline" size="sm" onClick={()=>setStudentsListDialogOpen(true)}><Users />{uiText("View students")}</Button><Button size="sm" onClick={()=>setNewSessionDialogOpen(true)}><Plus />{uiText("New session")}</Button></div></div>
            <AdminDashboardHeader
              title={t('common:admin.dashboard.title')}
              description={t('common:admin.dashboard.description')}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />

            <TrialCountdownTimer />

            <AdminTabs activeTab={activeTab} onTabChange={setActiveTab} paymentTab={paymentTab} overview={<div className="space-y-6">
              <DashboardOverview onPayments={() => { setPaymentTab('students'); setActiveTab('payments'); }} onNotify={() => setAnnouncementDialogOpen(true)} onStudents={() => setStudentsListDialogOpen(true)} />
              <AdminStats stats={stats} isLoading={isLoadingStats} />
              <AdminQuickActions onNewSession={() => setNewSessionDialogOpen(true)} onSendNotifications={() => setAnnouncementDialogOpen(true)} onViewStudents={() => setStudentsListDialogOpen(true)} />
            </div>} />

            <AdminDialogs
              newSessionDialogOpen={newSessionDialogOpen}
              setNewSessionDialogOpen={setNewSessionDialogOpen}
              studentsListDialogOpen={studentsListDialogOpen}
              setStudentsListDialogOpen={setStudentsListDialogOpen}
              announcementDialogOpen={announcementDialogOpen}
              setAnnouncementDialogOpen={setAnnouncementDialogOpen}
              fetchTrainingSessions={fetchTrainingSessions}
            />
          </div>
        </div>
      </Layout>
    </>
  );
};

export default AdminDashboard;
